import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { batchApi } from '../api';
import {
  Badge,
  Button,
  EmptyState,
  Loading,
  ProgressBar,
  Select,
  TextInput,
} from '../components/common';
import { AppShell } from '../components/layout/AppShell';
import { Sidebar } from '../components/layout/Sidebar';
import { TopBar } from '../components/layout/TopBar';
import { useModel } from '../context/ModelContext';
import { usePrompts } from '../context/PromptContext';
import { useToast } from '../context/ToastContext';
import { useJobList, useJobProgress } from '../hooks/useBatchJob';
import { useConversations } from '../hooks/useConversations';
import { useImageUpload } from '../hooks/useImageUpload';
import { formatPercent, relativeTime } from '../utils/format';
import './pages.css';

const STATUS_TONE = {
  running: 'ok',
  queued: 'default',
  completed: 'ok',
  failed: 'danger',
  cancelled: 'warn',
};

/**
 * Batch scoring: run one version over many images, export one CSV row per image.
 *
 * The throughput estimate is shown before the job starts, because the honest
 * answer to "how long will 200 images take" is roughly three seconds each on a
 * single GPU, and that changes whether someone waits or goes for lunch.
 */
export function BatchPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { activeId, readyVersions, workerAlive, isVllm, serving } = useModel();
  const { prompts } = usePrompts();
  const conversations = useConversations();
  const { jobs, refresh, loading, remove: removeJob, setPinned: pinJob } = useJobList();
  const upload = useImageUpload({ max: 2000 });

  const [name, setName] = useState('');
  const [versionId, setVersionId] = useState(activeId || '');
  const [promptId, setPromptId] = useState('');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dropping, setDropping] = useState(false);

  useEffect(() => {
    if (activeId && !versionId) setVersionId(activeId);
  }, [activeId, versionId]);

  // HF: the worker must be up, and switches to the job's version itself.
  // vLLM: the runner queues the job, and the version's base model must be serving.
  const chosenVersion = readyVersions.find((version) => version.id === (versionId || activeId));
  const servingState = chosenVersion?.serving?.state;
  const canRun = isVllm
    ? Boolean(serving?.model_server?.reachable) && servingState === 'ready'
    : workerAlive;
  const cannotRunReason = isVllm
    ? !serving?.model_server?.reachable
      ? 'The model server (vLLM) is not reachable.'
      : chosenVersion && servingState !== 'ready'
        ? `${chosenVersion.serving?.model_source || 'This version’s base model'} is ${
            servingState === 'starting' ? 'still loading' : 'not loaded'
          }. An administrator loads it in Settings → Model serving.`
        : !chosenVersion
          ? 'Choose a version.'
          : ''
    : 'The inference worker is not reachable — start it before queueing a job.';

  const { job, summary, cancel } = useJobProgress(selectedJobId, {
    onFinished: () => {
      refresh();
      toast.success('Batch finished', 'Results are ready to export.');
    },
  });

  const create = useCallback(async () => {
    if (!upload.imageIds.length) {
      toast.warn('No images', 'Add images before starting a job.');
      return;
    }
    setCreating(true);
    try {
      const created = await batchApi.create({
        name: name || `Batch ${upload.imageIds.length} images`,
        image_ids: upload.imageIds,
        version_id: versionId || undefined,
        prompt_id: promptId || undefined,
        mode: promptId ? undefined : 'extraction',
        start: true,
      });
      setSelectedJobId(created.id);
      upload.clear();
      setName('');
      await refresh();
      toast.success('Job started', `${created.total} images queued.`);
    } catch (cause) {
      toast.error(cause, { title: 'Could not start the job' });
    } finally {
      setCreating(false);
    }
  }, [name, promptId, refresh, toast, upload, versionId]);

  const estimate = upload.imageIds.length
    ? `${upload.imageIds.length} images ≈ ${Math.ceil((upload.imageIds.length * 3) / 60)} min at ~3 s each`
    : 'Add images to see an estimate';

  return (
    <AppShell
      sidebar={
        <Sidebar
          conversations={conversations.conversations}
          loading={conversations.loading}
          onNewAnalysis={() => navigate('/')}
          onSelectConversation={(id) => navigate(`/c/${id}`)}
          onDeleteConversation={conversations.remove}
          runningJobs={jobs.filter((item) => item.status === 'running').length}
        />
      }
    >
      <main className="page">
        <TopBar title="Batch scoring" />
        <div className="page__body">
          <div className="page__head">
            <div className="page__subtitle">
              Run one version over a folder of survey images, then export one row per image with one
              column per attribute. The GPU is serialised, so a batch and an interactive request
              take turns rather than competing.
            </div>
          </div>

          <div className="batch-grid">
            <section
              className={`card card--pad drop ${dropping ? 'drop--active' : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDropping(true);
              }}
              onDragLeave={() => setDropping(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDropping(false);
                upload.addFiles(event.dataTransfer.files);
              }}
            >
              <div className="drop__title">Drop images here</div>
              <div className="drop__hint">{estimate}</div>
              <label className="btn btn--sm" style={{ marginTop: 12 }}>
                Choose files
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="sr-only"
                  onChange={(event) => {
                    upload.addFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
              </label>
              {upload.uploading && <Loading label="Uploading…" />}
              {upload.items.length > 0 && (
                <>
                  <div className="drop__count">
                    {upload.items.length} image{upload.items.length === 1 ? '' : 's'} staged
                    <Button variant="ghost" size="sm" onClick={upload.clear}>
                      Clear
                    </Button>
                  </div>
                  <div className="drop__strip">
                    {upload.items.slice(0, 24).map((item) => (
                      <img key={item.id} src={item.previewUrl} alt={item.filename} />
                    ))}
                    {upload.items.length > 24 && (
                      <span className="drop__more">+{upload.items.length - 24}</span>
                    )}
                  </div>
                </>
              )}
            </section>

            <section className="card card--pad col" style={{ gap: 12 }}>
              <TextInput
                label="JOB NAME"
                value={name}
                placeholder="bh-schools-aug"
                onChange={(event) => setName(event.target.value)}
              />
              <Select
                label="VERSION"
                value={versionId}
                onChange={(event) => setVersionId(event.target.value)}
                placeholder="Use the loaded version"
                options={readyVersions.map((version) => {
                  const loaded = isVllm
                    ? version.serving?.state === 'ready'
                    : version.id === activeId;
                  return {
                    value: version.id,
                    label: `${version.label || version.id}${loaded ? ' (loaded)' : ''}`,
                  };
                })}
                hint={
                  !isVllm && versionId && versionId !== activeId
                    ? 'This version will be loaded when the job starts, which takes a second or two.'
                    : undefined
                }
              />
              <Select
                label="PROMPT"
                value={promptId}
                onChange={(event) => setPromptId(event.target.value)}
                placeholder="Generated extraction prompt (training parity)"
                options={prompts.map((prompt) => ({
                  value: prompt.id,
                  label: `${prompt.name}${prompt.is_extraction ? '' : ' (chat — no CSV columns)'}`,
                }))}
              />
              <Button
                variant="primary"
                onClick={create}
                loading={creating}
                disabled={!upload.imageIds.length || !canRun}
                block
              >
                Start job
              </Button>
              {!canRun && cannotRunReason && (
                <div className="xs" style={{ color: 'var(--state-warn)' }}>
                  {cannotRunReason}
                </div>
              )}
            </section>
          </div>

          {selectedJobId && job && (
            <section className="card card--pad" style={{ marginTop: 16 }}>
              <div className="row">
                <strong>{job.name}</strong>
                <Badge tone={STATUS_TONE[job.status]}>{job.status}</Badge>
                <span className="small muted">
                  {job.completed} / {job.total} · {job.failed} failed
                </span>
                <div className="grow" />
                {!['completed', 'failed', 'cancelled'].includes(job.status) && (
                  <Button size="sm" variant="danger" onClick={cancel}>
                    Cancel
                  </Button>
                )}
                <Button size="sm" onClick={() => batchApi.download(job.id, 'csv')}>
                  Export CSV
                </Button>
                <Button size="sm" variant="ghost" onClick={() => batchApi.download(job.id, 'json')}>
                  JSON
                </Button>
              </div>
              <div className="row" style={{ marginTop: 10 }}>
                <ProgressBar
                  value={job.completed}
                  max={job.total || 1}
                  danger={job.status === 'failed'}
                />
                <span className="small muted">{formatPercent(job.progress, 0)}</span>
              </div>
              {summary?.attribute_totals && (
                <div className="row small muted" style={{ marginTop: 10, gap: 16 }}>
                  <span>
                    Valid rate: <strong>{formatPercent(summary.valid_rate)}</strong>
                  </span>
                  <span>{summary.attribute_totals.ok} valid</span>
                  <span>{summary.attribute_totals.fuzzy} fuzzy</span>
                  <span>{summary.attribute_totals.unmatched} unmatched</span>
                  <span>{summary.attribute_totals.missing} missing</span>
                </div>
              )}
            </section>
          )}

          <h3 style={{ margin: '22px 0 10px', fontSize: 'var(--text-md)' }}>Jobs</h3>
          {loading ? (
            <Loading />
          ) : jobs.length === 0 ? (
            <EmptyState glyph="◲" title="No batch jobs yet">
              Stage some images above and start one.
            </EmptyState>
          ) : (
            <div className="card">
              <table className="table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Version</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((item) => (
                    <tr
                      key={item.id}
                      className={item.id === selectedJobId ? 'table__row--active' : ''}
                      onClick={() => setSelectedJobId(item.id)}
                    >
                      <td>
                        {item.is_pinned && (
                          <span className="conv__pinned" title="Pinned" aria-label="Pinned">
                            ◆
                          </span>
                        )}
                        {item.name}
                      </td>
                      <td className="mono muted">{item.version_id}</td>
                      <td>
                        <div className="row">
                          <ProgressBar
                            value={item.completed}
                            max={item.total || 1}
                            danger={item.status === 'failed'}
                          />
                          <span className="xs muted">
                            {item.completed}/{item.total}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[item.status]}>{item.status}</Badge>
                      </td>
                      <td className="muted small">{relativeTime(item.created_at)}</td>
                      <td>
                        <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          {item.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(event) => {
                                event.stopPropagation();
                                batchApi.download(item.id, 'csv');
                              }}
                            >
                              CSV
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            title={item.is_pinned ? 'Unpin' : 'Pin to the top'}
                            aria-label={`${item.is_pinned ? 'Unpin' : 'Pin'} ${item.name}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              pinJob(item.id, !item.is_pinned);
                            }}
                          >
                            {item.is_pinned ? '◆' : '◇'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title={
                              item.status === 'running'
                                ? 'Cancel the job before deleting it'
                                : 'Delete this job'
                            }
                            aria-label={`Delete ${item.name}`}
                            disabled={item.status === 'running'}
                            onClick={async (event) => {
                              event.stopPropagation();
                              const removed = await removeJob(item.id);
                              // Clear the detail pane if it was showing what we
                              // just deleted, or it keeps polling a 404.
                              if (removed && item.id === selectedJobId) setSelectedJobId(null);
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </AppShell>
  );
}
